/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { yupResolver } from '@hookform/resolvers/yup';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ObjectSchema } from 'yup';

import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import {
  defineNewServiceDesk,
  updateServiceDeskSettings
} from '../store/channels/reducer';
import { loadServiceDeskIntegration } from '../store/service-desks/actions';
import { selectDraftServiceDesk } from '../store/service-desks/selectors';
import { objectDifference } from '../util/helpers';

/**
 * Instatiate a service desk form with appropriate
 * hooks attached.
 *
 * @param {object} configSchema expected default configuration
 * @returns {object}
 */
export const useServiceDeskForm = (
  channelType: string,
  assistantId: string,
  environmentId: string | undefined,
  integrationId: string,
  settingsSchema: ObjectSchema<any>
) => {
  // Use one useForm & schema...
  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: settingsSchema.cast({}),
    resolver: yupResolver(settingsSchema)
  });
  const {
    control,
    reset,
    setValue,
    trigger,
    formState: { isValid, isDirty, errors, dirtyFields }
  } = formMethods;

  // Get the currently added draftServiceDesk. Which ever service desk is actively being editted is placed in redux here.
  // On closing the panel we reset this value.
  const draftServiceDesk = useAppSelector(selectDraftServiceDesk);

  // All the fields that react-hook-form is watching.
  const watchedFields = useWatch({ control: control });
  const dispatch = useAppDispatch();

  // Rather than having useEffects look at all changes to draftServiceDesk to see if it is defined, we just set this one
  // so it doesn't change all the time.
  const [hasDraftServiceDesk, setHasDraftServiceDesk] = useState<boolean>(
    !_.isNil(draftServiceDesk)
  );

  // Have we fed in the existing draftServiceDesk settings into react-hook-form once already.
  const [isPopulated, setIsPopulated] = useState<boolean>(false);

  const [formLoading, setFormLoading] = useState<boolean>(true);

  /**
   * Pass the given integration into the react-hook-form and reset the form validation/dirty states.
   */
  const resetFormWithServiceDeskSettings = useCallback(
    (integration, mergeWithRemote = false) => {
      const settings = settingsSchema.cast(
        integration?.settings
          ? _.cloneDeep(integration.settings)
          : JSON.parse(JSON.stringify(settingsSchema.cast({})))
      );

      let dirtyFields;

      /*
      Here we will compare the passed settings to what is in redux.
      We then will update the differing fields in react-hook-form with what is in redux.
      This has to happen if someone makes edit to the service desk fields and then leaves to go to another tab.
      If we just grab what is in redux, react-hook-form has lost track of what fields are dirty.
      */
      if (mergeWithRemote && draftServiceDesk?.settings) {
        // Get the difference between what is currently in draftServiceDesk and what is is previously stored in the server.
        dirtyFields = objectDifference(settings, draftServiceDesk.settings);
      }

      // Reset the form to defaults.
      reset(settings);

      if (mergeWithRemote && dirtyFields) {
        // For each different item, set a new value in react-hook-form based on what is in redux currently.
        for (const [key] of Object.entries(dirtyFields)) {
          setValue(key, _.get(draftServiceDesk.settings, key));
        }
      }
      trigger();
      setIsPopulated(true);
      setFormLoading(false);
    },
    [draftServiceDesk?.settings, reset, setValue, settingsSchema, trigger]
  );

  /**
   * If this is a new service desk integration, we need to add it add it as the draftServiceDesk.
   */
  const populateNewIntegration = useCallback(() => {
    const serviceDesk = dispatch(
      defineNewServiceDesk({
        integration: {
          name: 'haa_' + new Date().toISOString(), // required for integration name validation
          description: '',
          type: channelType,
          settings: JSON.parse(JSON.stringify(settingsSchema.cast({})))
        }
      })
    );
    resetFormWithServiceDeskSettings(serviceDesk.payload.integration);
  }, [channelType, dispatch, resetFormWithServiceDeskSettings, settingsSchema]);

  /**
   * If this is not a new integration we either need to load it from the server, or if it has already been loaded,
   * we make sure that the react-hook-form is rehydrated. This latter condition can happen if someone visits the
   * live agent tab, leaves to another tab on the web chat page, and then comes back.
   */
  const populateExistingIntegration = useCallback(async () => {
    if (integrationId === draftServiceDesk?.integration_id) {
      if (!isPopulated) {
        /*
        We want to grab the old values so we can compare with what is in redux when we reset the form.
        We have to do this because if we are in this state the react-hook-form has been blown away and has lost track
        of what values are dirty. We need to grab what is on the server to do the comparison.
        */
        setFormLoading(true);
        const { payload } = await dispatch(
          loadServiceDeskIntegration({
            assistantId,
            environmentId,
            integrationId,
            updateServiceDeskDraft: false
          })
        );
        resetFormWithServiceDeskSettings(payload, true);
      }
    } else {
      setFormLoading(true);
      const { payload } = await dispatch(
        loadServiceDeskIntegration({
          assistantId,
          environmentId,
          integrationId
        })
      );
      resetFormWithServiceDeskSettings(payload, true);
    }
  }, [
    integrationId,
    draftServiceDesk,
    isPopulated,
    dispatch,
    assistantId,
    environmentId,
    resetFormWithServiceDeskSettings
  ]);

  /**
   * If a different integrationID is passed in we switch from "new" to "existing" mode.
   */
  useEffect(() => {
    if (!integrationId) {
      // But don't re-create a new integration if we have already run it!
      if (isPopulated) {
        return;
      }
      populateNewIntegration();
    } else {
      populateExistingIntegration();
    }
  }, [
    integrationId,
    populateExistingIntegration,
    populateNewIntegration,
    isPopulated
  ]);

  /**
   * Get the currently added draftServiceDesk. Which ever service desk is actively being editted is placed in redux here.
   * On closing the panel we reset this value.
   */
  useEffect(() => {
    setHasDraftServiceDesk(!_.isNil(draftServiceDesk));
  }, [draftServiceDesk]);

  /**
   * As changes get made in the react-hook-form, make sure to copy them over to draftServiceDesk.
   * This is debounced at 50ms.
   */
  useEffect(() => {
    // Debouncing the updates
    const handler = setTimeout(() => {
      // Only update the draft if the draft is defined
      if (hasDraftServiceDesk && isPopulated) {
        /*
        The formState's isValid and isDirty fields fluctuate between
        false and true during loading time, so we need to check whether
        the settings are touched or have errors.
        */
        const settingsIsInvalid = !isValid && !_.isEmpty(errors);
        const settingsIsDirty = isDirty && !_.isEmpty(dirtyFields);

        console.log('useServiceDeskHelper useSyncDraftConfig:', {
          settingsIsValid: !settingsIsInvalid,
          settingsIsDirty,
          errors,
          dirtyFields,
          watchedFields
        });

        dispatch(
          updateServiceDeskSettings({
            settings: JSON.parse(JSON.stringify(watchedFields)),
            isValid: !settingsIsInvalid,
            isDirty: settingsIsDirty
          })
        );
      }
      // cleans up debounce
    }, 50);
    // When useEffect ends;
    return () => {
      clearTimeout(handler);
    };
  }, [
    dirtyFields,
    dispatch,
    hasDraftServiceDesk,
    errors,
    isDirty,
    isValid,
    watchedFields,
    isPopulated
  ]);

  return { formMethods, formLoading };
};
